import React, {useState} from 'react';

import {hooks} from '../hooks';
import {SonsType} from '../types';
import {components} from '../components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export const DetailSonsGradeAnalysis: React.FC = () => {
  const navigate = hooks.useNavigate();
  const dispatch = hooks.useDispatch();
  const location = hooks.useLocation();

  const son: SonsType = location.state.son;

  const [opacity, setOpacity] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"average" | "subject">("average");

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        showGoBack={true}
        showBasket={true}
        title='Child Grade Analysis'
      />
    );
  };

  const renderDetails = (): JSX.Element => {
    return (
      <section style={{display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', marginTop: 10, paddingTop: 8, paddingBottom: 40, gap: 15}}>
          <span onClick={() => navigate(`/detail-sons/detail-sons-absensi/${son.id}`, {state: {son}})}>
            <img style={{maxWidth: 50, padding: 10, borderRadius: 6, border: '1px solid var(--border-color)', backgroundColor: 'var(--main-turquoise)'}} className='clickable' src="/assets/img-icon/absensi-icon-white.png" alt="Absensi Icon" />
            <p className='t10' style={{textAlign: 'center'}}>Absensi</p>
          </span>
          <span onClick={() => navigate(`/detail-sons/detail-sons-rkh/${son.id}`, {state: {son}})}>
            <img style={{maxWidth: 50, padding: 10, borderRadius: 6, border: '1px solid var(--border-color)', backgroundColor: 'var(--main-turquoise)'}} className='clickable' src="/assets/img-icon/rkh-icon-white.png" alt="RKH Icon" />
            <p className='t10' style={{textAlign: 'center'}}>RKH</p>
          </span>
          <span onClick={() => navigate(`/detail-sons/detail-sons-raport/${son.id}`, {state: {son}})}>
            <img style={{maxWidth: 50, padding: 10, borderRadius: 6, border: '1px solid var(--border-color)', backgroundColor: 'var(--main-turquoise)'}} className='clickable' src="/assets/img-icon/raport-icon-white.png" alt="Raport Icon" />
            <p className='t10' style={{textAlign: 'center'}}>Raport</p>
          </span>
      </section>
    );
  };

  const renderImage = (): JSX.Element => {
    return (
      <section style={{position: 'relative'}}>
        <img
          src={son.banner}
          alt={son.name}
          style={{
            width: '100%', 
            borderRadius: 6,
            objectFit: 'cover',
            maxHeight: 260,
            objectPosition: 'top center'
          }}
        />
      </section>
    );
  };

  const renderChartScore = (): JSX.Element => {
    // helper to map your API data into recharts format
    const mapData = (
      arr: Array<{
        id: number;
        name: string;
        highest_score: string;
        average_score: string;
        child_score: string;
      }>
    ) =>
      arr.map((d) => ({
        name: d.name,
        Tertinggi: Number(d.highest_score),
        RataRata: Number(d.average_score),
        Ananda: Number(d.child_score),
      }));

    const finalData = mapData(son.AverageFinalScore);
    const knowledgeData = mapData(son.AverageKnowledgeScore);
    const skillsData = mapData(son.AverageSkillsScore);

    const mathFinalData = mapData(son.AverageFinalMathSubjectsScore);
    const bahasaFinalData = mapData(son.AverageFinalBahasaSubjectsScore);
    const ipaFinalData = mapData(son.AverageFinalIpaSubjectsScore);

    // reusable chart component
    const ChartCard = ({
      title,
      subtitle,
      data,
    }: {
      title: string;
      subtitle: string;
      data: any[];
    }) => (
      <div className='chart-card'>
        <div className='chart-header'>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart 
            data={data}
            margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" stroke="#9ca3af" />
            <YAxis domain={[70, 100]} stroke="#9ca3af" width={40} />
            <Tooltip contentStyle={{}} wrapperClassName="custom-tooltip" />
            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{}}
              className="custom-legend"
            />
            <Line
              type="monotone"
              dataKey="Tertinggi"
              stroke="#ef4444"
              strokeWidth={3}
              dot={{ r: 5, strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="RataRata"
              stroke="#3b82f6"
              strokeWidth={3}
              dot={{ r: 5, strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="Ananda"
              stroke="#8b5cf6"
              strokeWidth={3}
              dot={{ r: 5, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );

    return (
      <section className="chart-section">
        {/* --- Tab Switcher --- */}
        <div className="tab-switcher">
          <button
            className={activeTab === "average" ? "tab active" : "tab"}
            onClick={() => setActiveTab("average")}
          >
            Final Score
          </button>
          <button
            className={activeTab === "subject" ? "tab active" : "tab"}
            onClick={() => setActiveTab("subject")}
          >
            Subjects Score
          </button>
        </div>

        {/* --- Tab Content --- */}
        {activeTab === "average" && (
          <>
            <ChartCard
              title="Rata-Rata Nilai Akhir"
              subtitle="Rata-Rata Pengetahuan & Keterampilan"
              data={finalData}
            />
            <ChartCard
              title="Rata-Rata Pengetahuan"
              subtitle="Rata-Rata Nilai Pengetahuan"
              data={knowledgeData}
            />
            <ChartCard
              title="Rata-Rata Keterampilan"
              subtitle="Rata-Rata Nilai Keterampilan"
              data={skillsData}
            />
          </>
        )}

        {activeTab === "subject" && (
          <>
            <ChartCard
              title="Matematika"
              subtitle="Nilai Matematika"
              data={mathFinalData}
            />
            <ChartCard
              title="Bahasa Indonesia"
              subtitle="Nilai Bahasa Indonesia"
              data={bahasaFinalData}
            />
            <ChartCard
              title="Ilmu Pengetahuan Alam"
              subtitle="Nilai Ilmu Pengetahuan Alam"
              data={ipaFinalData}
            />
          </>
        )}
      </section>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <main className='scrollable container'>
        <ul style={{paddingTop: 10, paddingBottom: 20}}>
          <li
           style={{
            padding: '0 12px',
            borderRadius: 10,
            position: 'relative',
            marginBottom: 15,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
          }}>
            <div
              style={{
                width: '100%',
              }}
            >
              <section style={{minWidth: 140, marginBottom: 15, borderRadius: 6, position: 'relative'}}>
                <div 
                  className='row-center-space-between' 
                  style={{
                    padding: 10, 
                    background: 'linear-gradient(180deg,rgba(255, 255, 255, 0) 50%, rgba(157, 160, 153, 0.4) 75%, rgba(45, 51, 37, 0.8) 100%)',
                    borderRadius: 6, 
                    position: 'absolute',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    alignItems: 'flex-end',
                    color: 'var(--main-turquoise)'
                  }}>
                  <h4 style={{color: 'white'}}>{son.name}</h4>
                </div>
                {renderImage()}
              </section>
              {renderChartScore()}
              {renderDetails()}
            </div>
          </li>
        </ul>
      </main>
    );
  };
  const renderFooter = (): JSX.Element => {
    return <components.Footer />;
  };

  return (
    <div
      id='screen'
      style={{opacity}}
    >
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </div>
  );
};
